import React, { FormEvent, useEffect, useState } from "react";
import emailjs from "@emailjs/browser";
import {
  ArrowRight,
  Boxes,
  ClipboardCheck,
  Clock3,
  DollarSign,
  Factory,
  HelpCircle,
  MapPin,
  Monitor,
  PackageCheck,
  PackageOpen,
  Phone,
  ScanLine,
  Search,
  Send,
  Settings,
  ShieldCheck,
  Truck,
  Warehouse,
  Workflow,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useToast } from "../hooks/use-toast";

type AutomationFormData = {
  companyName: string;
  contactName: string;
  phone: string;
  email: string;
  region: string;
  centerType: string;
  equipment: string[];
  deploymentMethod: string;
  volume: string;
  timeline: string;
  message: string;
  privacyAgreed: boolean;
};

type IconItem = {
  title: string;
  description: string;
  icon: LucideIcon;
};

type TextItem = {
  title: string;
  description: string;
};

type FaqItem = {
  question: string;
  answer: string;
};

type HeroStat = {
  label: string;
  value: string;
};

type EmailLabels = Record<string, string>;

const initialFormData: AutomationFormData = {
  companyName: "",
  contactName: "",
  phone: "",
  email: "",
  region: "",
  centerType: "",
  equipment: [],
  deploymentMethod: "",
  volume: "",
  timeline: "",
  message: "",
  privacyAgreed: false,
};

const problemIcons = [DollarSign, Search, Workflow, Wrench] as const;
const serviceScopeIcons = [ClipboardCheck, Settings, Monitor, ShieldCheck] as const;
const equipmentIcons = [Truck, Workflow, Factory, Monitor, PackageOpen, ScanLine, Settings] as const;
const customerTargetIcons = [Warehouse, Boxes, PackageCheck, Truck, Factory] as const;

const withIcons = (items: TextItem[], icons: readonly LucideIcon[]): IconItem[] =>
  items.map((item, index) => ({
    ...item,
    icon: icons[index] ?? HelpCircle,
  }));

const scrollToInquiry = () => {
  document.getElementById("automation-inquiry")?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const formatInquiryMessage = (data: AutomationFormData, labels: EmailLabels) => {
  return [
    labels.subject,
    `${labels.companyName}: ${data.companyName}`,
    `${labels.contactName}: ${data.contactName}`,
    `${labels.phone}: ${data.phone}`,
    `${labels.email}: ${data.email}`,
    `${labels.region}: ${data.region}`,
    `${labels.centerType}: ${data.centerType}`,
    `${labels.equipment}: ${data.equipment.join(", ") || labels.notSelected}`,
    `${labels.deploymentMethod}: ${data.deploymentMethod}`,
    `${labels.volume}: ${data.volume}`,
    `${labels.timeline}: ${data.timeline}`,
    "",
    labels.messageTitle,
    data.message,
    "",
    `${labels.privacy}: ${data.privacyAgreed ? labels.agreed : labels.notAgreed}`,
  ].join("\n");
};

export default function VandroAutomation() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [formData, setFormData] = useState<AutomationFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const problemItems = withIcons(
    t("vandroAutomation.problemItems", { returnObjects: true }) as TextItem[],
    problemIcons
  );
  const serviceScope = withIcons(
    t("vandroAutomation.serviceScope", { returnObjects: true }) as TextItem[],
    serviceScopeIcons
  );
  const equipmentCategories = withIcons(
    t("vandroAutomation.equipmentCategories", { returnObjects: true }) as TextItem[],
    equipmentIcons
  );
  const customerTargets = withIcons(
    t("vandroAutomation.customerTargets", { returnObjects: true }) as TextItem[],
    customerTargetIcons
  );
  const softwareFlow = t("vandroAutomation.softwareFlow", { returnObjects: true }) as string[];
  const adoptionSteps = t("vandroAutomation.adoptionSteps", { returnObjects: true }) as string[];
  const faqs = t("vandroAutomation.faqs", { returnObjects: true }) as FaqItem[];
  const equipmentOptions = equipmentCategories.map(({ title }) => title);
  const centerTypes = t("vandroAutomation.options.centerTypes", { returnObjects: true }) as string[];
  const deploymentMethods = t("vandroAutomation.options.deploymentMethods", { returnObjects: true }) as string[];
  const timelineOptions = t("vandroAutomation.options.timelines", { returnObjects: true }) as string[];
  const heroStats = t("vandroAutomation.hero.stats", { returnObjects: true }) as HeroStat[];
  const heroTitleLines = t("vandroAutomation.hero.titleLines", { returnObjects: true }) as string[];
  const heroSubtitleLines = t("vandroAutomation.hero.subtitleLines", { returnObjects: true }) as string[];
  const inquiryTitleLines = t("vandroAutomation.inquiry.titleLines", { returnObjects: true }) as string[];
  const inquiryDescriptionLines = t("vandroAutomation.inquiry.descriptionLines", { returnObjects: true }) as string[];
  const inquiryNotes = t("vandroAutomation.inquiry.notes", { returnObjects: true }) as string[];
  const inquiryFields = t("vandroAutomation.inquiry.fields", { returnObjects: true }) as Record<string, string>;
  const emailLabels = t("vandroAutomation.email", { returnObjects: true }) as EmailLabels;

  useEffect(() => {
    document.title = t("vandroAutomation.meta.title");
  }, [t]);

  useEffect(() => {
    emailjs.init("E7XMCz1wH96XFzgwU");

    if (window.location.hash === "#automation-inquiry" || window.location.hash === "#rental-inquiry") {
      window.setTimeout(scrollToInquiry, 100);
    }
  }, []);

  const updateField = (field: keyof AutomationFormData, value: string | boolean | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleEquipment = (label: string) => {
    setFormData((prev) => ({
      ...prev,
      equipment: prev.equipment.includes(label)
        ? prev.equipment.filter((item) => item !== label)
        : [...prev.equipment, label],
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitMessage(null);

    if (!formData.privacyAgreed) {
      setSubmitMessage(t("vandroAutomation.messages.privacyRequired"));
      showToast(t("vandroAutomation.messages.privacyRequired"), "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await emailjs.send("service_3bllw4p", "template_jlblth9", {
        from_name: `${formData.contactName} (${formData.companyName})`,
        from_email: formData.email,
        subject: t("vandroAutomation.email.subjectWithCompany", { company: formData.companyName }),
        message: formatInquiryMessage(formData, emailLabels),
        company_name: formData.companyName,
        phone: formData.phone,
        region: formData.region,
        center_type: formData.centerType,
        equipment: formData.equipment.join(", "),
        deployment_method: formData.deploymentMethod,
        volume: formData.volume,
        timeline: formData.timeline,
      });

      if (result.status === 200) {
        setSubmitMessage(t("vandroAutomation.messages.success"));
        showToast(t("vandroAutomation.messages.successToast"), "success");
        setFormData(initialFormData);
      }
    } catch (error) {
      setSubmitMessage(t("vandroAutomation.messages.error"));
      showToast(t("vandroAutomation.messages.errorToast"), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-zinc-50 text-zinc-900 [word-break:keep-all]">
      <section className="relative min-h-screen overflow-hidden bg-zinc-950 px-4 pt-28 text-white sm:px-6 lg:px-8">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-45"
          style={{ backgroundImage: "url('/vandro-automation-hero.jpg')" }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(9,9,11,0.92),rgba(9,9,11,0.72),rgba(9,9,11,0.36))]" aria-hidden />

        <div className="relative z-10 mx-auto flex min-h-[calc(100vh-7rem)] max-w-7xl flex-col justify-center py-16">
          <div className="min-w-0 max-w-4xl">
            <img src="/vandro-logo.png" alt="VANDRO" className="mb-9 h-11 w-auto brightness-0 invert sm:h-14" />
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.24em] text-[#ffb08a]">
              {t("vandroAutomation.hero.eyebrow")}
            </p>
            <h1 className="max-w-full text-3xl font-semibold leading-tight tracking-tight text-white [overflow-wrap:anywhere] sm:max-w-4xl sm:text-5xl lg:text-6xl">
              {heroTitleLines.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </h1>
            <p className="mt-6 max-w-[calc(100vw-2rem)] text-base leading-8 text-zinc-200 break-words sm:max-w-3xl sm:text-xl">
              {heroSubtitleLines.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </p>
            <div className="mt-10 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={scrollToInquiry}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#E36330] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#c85229] sm:w-auto"
              >
                {t("vandroAutomation.hero.primaryCta")}
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={scrollToInquiry}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/55 bg-white/10 px-6 py-3 text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/20 sm:w-auto"
              >
                {t("vandroAutomation.hero.secondaryCta")}
              </button>
            </div>
          </div>

          <div className="mt-16 grid max-w-5xl grid-cols-1 gap-3 sm:grid-cols-3">
            {heroStats.map((item) => (
              <div key={item.label} className="border-l-2 border-[#E36330] bg-zinc-950/45 px-5 py-4 backdrop-blur-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">{item.label}</p>
                <p className="mt-1 text-lg font-semibold text-white">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-zinc-200 bg-white px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-10 max-w-3xl">
            <p className="text-sm font-semibold text-[#E36330]">{t("vandroAutomation.sections.painEyebrow")}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">
              {t("vandroAutomation.sections.painTitle")}
            </h2>
          </div>
          <div className="grid gap-4 md:grid-cols-4">
            {problemItems.map(({ title, description, icon: Icon }) => (
              <article key={title} className="rounded-lg border border-zinc-200 bg-zinc-50 p-6">
                <Icon className="h-8 w-8 text-[#E36330]" />
                <h3 className="mt-5 text-lg font-semibold text-zinc-900">{title}</h3>
                <p className="mt-3 text-sm leading-6 text-zinc-600">{description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-zinc-50 px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
          <div>
            <p className="text-sm font-semibold text-[#E36330]">{t("vandroAutomation.sections.scopeEyebrow")}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">
              {t("vandroAutomation.sections.scopeTitle")}
            </h2>
            <p className="mt-5 text-base leading-8 text-zinc-600">
              {t("vandroAutomation.sections.scopeDescription")}
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {serviceScope.map(({ title, description, icon: Icon }) => (
              <article key={title} className="rounded-lg border border-zinc-200 bg-white p-6 shadow-sm">
                <Icon className="h-7 w-7 text-blue-600" />
                <h3 className="mt-5 text-lg font-semibold text-zinc-900">{title}</h3>
                <p className="mt-3 text-sm leading-6 text-zinc-600">{description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-sm font-semibold text-[#E36330]">{t("vandroAutomation.sections.equipmentEyebrow")}</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">
                {t("vandroAutomation.sections.equipmentTitle")}
              </h2>
            </div>
            <p className="max-w-xl text-sm leading-6 text-zinc-600">
              {t("vandroAutomation.sections.equipmentDescription")}
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {equipmentCategories.map(({ title, description, icon: Icon }) => (
              <article key={title} className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#E36330]/10 text-[#E36330]">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-base font-semibold text-zinc-900">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-zinc-600">{description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-zinc-200 bg-zinc-950 px-4 py-20 text-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 max-w-3xl">
            <p className="text-sm font-semibold text-[#ffb08a]">{t("vandroAutomation.sections.softwareEyebrow")}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {t("vandroAutomation.sections.softwareTitle")}
            </h2>
            <p className="mt-5 text-base leading-8 text-zinc-300">
              {t("vandroAutomation.sections.softwareDescription")}
            </p>
          </div>

          <div className="grid gap-3 lg:grid-cols-[repeat(5,minmax(0,1fr))]">
            {softwareFlow.map((step, index) => (
              <div key={step} className="relative">
                <div className="flex min-h-32 flex-col justify-between rounded-lg border border-white/15 bg-white/10 p-5 backdrop-blur-sm">
                  <span className="text-xs font-semibold text-[#ffb08a]">{String(index + 1).padStart(2, "0")}</span>
                  <p className="mt-6 text-lg font-semibold text-white">{step}</p>
                </div>
                {index < softwareFlow.length - 1 && (
                  <ArrowRight className="mx-auto my-2 h-5 w-5 text-[#ffb08a] lg:absolute lg:-right-5 lg:top-1/2 lg:z-10 lg:mx-0 lg:my-0 lg:-translate-y-1/2" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 max-w-3xl">
            <p className="text-sm font-semibold text-[#E36330]">{t("vandroAutomation.sections.processEyebrow")}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">
              {t("vandroAutomation.sections.processTitle")}
            </h2>
          </div>
          <div className="grid gap-4 md:grid-cols-7">
            {adoptionSteps.map((step, index) => (
              <article key={step} className="rounded-lg border border-zinc-200 bg-zinc-50 p-5">
                <span className="text-xs font-semibold text-[#E36330]">{index + 1}</span>
                <h3 className="mt-4 text-sm font-semibold leading-6 text-zinc-900">{step}</h3>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-zinc-50 px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-10 max-w-3xl">
            <p className="text-sm font-semibold text-[#E36330]">{t("vandroAutomation.sections.targetsEyebrow")}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">
              {t("vandroAutomation.sections.targetsTitle")}
            </h2>
          </div>
          <div className="grid gap-4 md:grid-cols-5">
            {customerTargets.map(({ title, description, icon: Icon }) => (
              <article key={title} className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
                <Icon className="h-7 w-7 text-emerald-600" />
                <h3 className="mt-4 text-base font-semibold text-zinc-900">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-zinc-600">{description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.85fr_1.15fr]">
          <div>
            <p className="text-sm font-semibold text-[#E36330]">{t("vandroAutomation.sections.faqEyebrow")}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">
              {t("vandroAutomation.sections.faqTitle")}
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map(({ question, answer }) => (
              <details key={question} className="group rounded-lg border border-zinc-200 bg-zinc-50 p-5">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-base font-semibold text-zinc-900">
                  <span className="flex items-center gap-3">
                    <HelpCircle className="h-5 w-5 shrink-0 text-[#E36330]" />
                    {question}
                  </span>
                  <span className="text-zinc-400 transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-4 text-sm leading-7 text-zinc-600">{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section id="automation-inquiry" className="bg-zinc-950 px-4 py-20 text-white sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.85fr_1.15fr]">
          <div>
            <p className="text-sm font-semibold text-[#ffb08a]">{t("vandroAutomation.inquiry.eyebrow")}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight [word-break:keep-all] sm:text-4xl">
              {inquiryTitleLines.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </h2>
            <p className="mt-5 text-base leading-8 text-zinc-300 [word-break:keep-all]">
              {inquiryDescriptionLines.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </p>
            <div className="mt-8 space-y-4 text-sm text-zinc-300">
              {inquiryNotes.map((note, index) => {
                const NoteIcon = [Phone, MapPin, Clock3][index] ?? Phone;
                return (
                  <p key={note} className="flex items-center gap-3 [word-break:keep-all]">
                    <NoteIcon className="h-5 w-5 text-[#ffb08a]" />
                    <span>{note}</span>
                  </p>
                );
              })}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="rounded-lg border border-white/15 bg-white p-6 text-zinc-900 shadow-2xl sm:p-8">
            <div className="grid gap-5 sm:grid-cols-2">
              <label className="block text-sm font-medium text-zinc-700">
                {inquiryFields.companyName}
                <input
                  required
                  value={formData.companyName}
                  onChange={(event) => updateField("companyName", event.target.value)}
                  className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm outline-none transition focus:border-[#E36330] focus:ring-2 focus:ring-[#E36330]/20"
                />
              </label>
              <label className="block text-sm font-medium text-zinc-700">
                {inquiryFields.contactName}
                <input
                  required
                  value={formData.contactName}
                  onChange={(event) => updateField("contactName", event.target.value)}
                  className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm outline-none transition focus:border-[#E36330] focus:ring-2 focus:ring-[#E36330]/20"
                />
              </label>
              <label className="block text-sm font-medium text-zinc-700">
                {inquiryFields.phone}
                <input
                  required
                  value={formData.phone}
                  onChange={(event) => updateField("phone", event.target.value)}
                  className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm outline-none transition focus:border-[#E36330] focus:ring-2 focus:ring-[#E36330]/20"
                />
              </label>
              <label className="block text-sm font-medium text-zinc-700">
                {inquiryFields.email}
                <input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(event) => updateField("email", event.target.value)}
                  className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm outline-none transition focus:border-[#E36330] focus:ring-2 focus:ring-[#E36330]/20"
                />
              </label>
              <label className="block text-sm font-medium text-zinc-700">
                {inquiryFields.region}
                <input
                  required
                  value={formData.region}
                  onChange={(event) => updateField("region", event.target.value)}
                  className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm outline-none transition focus:border-[#E36330] focus:ring-2 focus:ring-[#E36330]/20"
                />
              </label>
              <label className="block text-sm font-medium text-zinc-700">
                {inquiryFields.centerType}
                <select
                  required
                  value={formData.centerType}
                  onChange={(event) => updateField("centerType", event.target.value)}
                  className="mt-2 w-full rounded-md border border-zinc-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-[#E36330] focus:ring-2 focus:ring-[#E36330]/20"
                >
                  <option value="">{t("vandroAutomation.inquiry.selectPlaceholder")}</option>
                  {centerTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <fieldset className="mt-5">
              <legend className="text-sm font-medium text-zinc-700">{inquiryFields.equipment}</legend>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {equipmentOptions.map((option) => (
                  <label key={option} className="flex items-center gap-2 rounded-md border border-zinc-200 px-3 py-2 text-sm text-zinc-700">
                    <input
                      type="checkbox"
                      checked={formData.equipment.includes(option)}
                      onChange={() => toggleEquipment(option)}
                      className="h-4 w-4 rounded border-zinc-300 text-[#E36330] focus:ring-[#E36330]"
                    />
                    {option}
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <label className="block text-sm font-medium text-zinc-700">
                {inquiryFields.deploymentMethod}
                <select
                  required
                  value={formData.deploymentMethod}
                  onChange={(event) => updateField("deploymentMethod", event.target.value)}
                  className="mt-2 w-full rounded-md border border-zinc-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-[#E36330] focus:ring-2 focus:ring-[#E36330]/20"
                >
                  <option value="">{t("vandroAutomation.inquiry.selectPlaceholder")}</option>
                  {deploymentMethods.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm font-medium text-zinc-700">
                {inquiryFields.volume}
                <input
                  required
                  value={formData.volume}
                  onChange={(event) => updateField("volume", event.target.value)}
                  placeholder={t("vandroAutomation.inquiry.volumePlaceholder")}
                  className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm outline-none transition focus:border-[#E36330] focus:ring-2 focus:ring-[#E36330]/20"
                />
              </label>
              <label className="block text-sm font-medium text-zinc-700 sm:col-span-2">
                {inquiryFields.timeline}
                <select
                  required
                  value={formData.timeline}
                  onChange={(event) => updateField("timeline", event.target.value)}
                  className="mt-2 w-full rounded-md border border-zinc-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-[#E36330] focus:ring-2 focus:ring-[#E36330]/20"
                >
                  <option value="">{t("vandroAutomation.inquiry.selectPlaceholder")}</option>
                  {timelineOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="mt-5 block text-sm font-medium text-zinc-700">
              {inquiryFields.message}
              <textarea
                required
                rows={5}
                value={formData.message}
                onChange={(event) => updateField("message", event.target.value)}
                placeholder={t("vandroAutomation.inquiry.messagePlaceholder")}
                className="mt-2 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm outline-none transition focus:border-[#E36330] focus:ring-2 focus:ring-[#E36330]/20"
              />
            </label>

            <label className="mt-5 flex items-start gap-3 rounded-md bg-zinc-50 p-4 text-sm text-zinc-700">
              <input
                required
                type="checkbox"
                checked={formData.privacyAgreed}
                onChange={(event) => updateField("privacyAgreed", event.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-[#E36330] focus:ring-[#E36330]"
              />
              <span>{inquiryFields.privacy}</span>
            </label>

            {submitMessage && (
              <p className="mt-4 rounded-md bg-zinc-100 px-4 py-3 text-sm text-zinc-700">{submitMessage}</p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#E36330] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#c85229] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? t("vandroAutomation.inquiry.submitting") : t("vandroAutomation.inquiry.submit")}
              {isSubmitting ? null : <Send className="h-4 w-4" />}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
